import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import cs from "classnames";
import styles from "./styles.module.scss";
import {
  ModerationIcon,
  LaunchIcon,
  ActiveIcon,
  PausedIcon,
  DoneIcon,
  CanceledIcon,
  ChevronIcon,
} from "./icons";
import {
  OrderMetaProps,
  OrderProgressBarProps,
  OrderStatusAction,
  OrderStatusBadgeProps,
  OrderStatusDropdownProps,
  OrderStatusMenuItem,
  OrderStatusMenuProps,
  OrderTimelineRowProps,
  OrderTimelineStatus,
} from "./types";

export const TONE: Record<OrderTimelineStatus, string> = {
  moderation: styles["tone-moderation"],
  launch: styles["tone-launch"],
  active: styles["tone-active"],
  paused: styles["tone-paused"],
  done: styles["tone-done"],
  canceled: styles["tone-canceled"],
};

const STATUS_LABEL: Record<OrderTimelineStatus, string> = {
  moderation: "Moderation",
  launch: "Launch",
  active: "Active",
  paused: "Paused",
  done: "Done",
  canceled: "Canceled",
};

const STATUS_ICON: Record<
  OrderTimelineStatus,
  React.FC<React.SVGProps<SVGSVGElement>>
> = {
  moderation: ModerationIcon,
  launch: LaunchIcon,
  active: ActiveIcon,
  paused: PausedIcon,
  done: DoneIcon,
  canceled: CanceledIcon,
};

// Statuses rendered as a dropdown in the design — only these get a chevron.
const WITH_CHEVRON: ReadonlySet<OrderTimelineStatus> = new Set([
  "launch",
  "active",
  "paused",
]);

/**
 * Status pill (Figma Status-dropdown, 113:4300/4301/4303/4362/4380).
 * Presentational by default; pass `onClick` to make it the interactive
 * trigger for a container-owned status menu.
 */
export const OrderStatusBadge: React.FC<OrderStatusBadgeProps> = ({
  status,
  label,
  onClick,
  open = false,
  chevron: showChevron,
  className,
}) => {
  const Icon = STATUS_ICON[status];
  const chevron = showChevron ?? WITH_CHEVRON.has(status);

  const content = (
    <>
      <span className={styles.badgeMain}>
        <Icon className={styles.badgeIcon} />
        <span className={styles.badgeLabel}>{label ?? STATUS_LABEL[status]}</span>
      </span>
      {chevron && (
        <ChevronIcon className={cs(styles.chevron, { [styles.chevronUp]: open })} />
      )}
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        className={cs(styles.badge, styles.badgeButton, TONE[status], className)}
        onClick={onClick}
      >
        {content}
      </button>
    );
  }
  return (
    <span className={cs(styles.badge, TONE[status], className)}>{content}</span>
  );
};

/** Progress bar with a centered % label (Figma Progress bar New, 158:53xxx). */
export const OrderProgressBar: React.FC<OrderProgressBarProps> = ({
  status,
  percent,
  className,
}) => {
  const pct = Math.max(0, Math.min(100, Math.round(percent)));
  return (
    <div className={cs(styles.progress, TONE[status], className)}>
      <span className={styles.progressFill} style={{ width: `${pct}%` }} />
      <span className={styles.progressText}>{pct} %</span>
    </div>
  );
};

/** Order number + type pill column (Figma Orders + Label, 112:42xx). */
export const OrderMeta: React.FC<OrderMetaProps> = ({
  id,
  label,
  status,
  className,
}) => (
  <div className={cs(styles.meta, TONE[status], className)}>
    <span className={styles.orderId}># {id}</span>
    <span className={styles.pill}>{label}</span>
  </div>
);

/**
 * Full status-toned order row: meta + progress + status control
 * (Figma Order-Timeline-view, 113:4450-4454).
 */
export const OrderTimelineRow: React.FC<OrderTimelineRowProps> = ({
  id,
  label,
  status,
  percent,
  statusSlot,
  onStatusClick,
  className,
}) => (
  <div className={cs(styles.row, TONE[status], className)}>
    <OrderMeta id={id} label={label} status={status} />
    <OrderProgressBar status={status} percent={percent} />
    {statusSlot ?? <OrderStatusBadge status={status} onClick={onStatusClick} />}
  </div>
);

// ---- status dropdown menu (Figma 113:4304) ----

const ACTION_LABEL: Record<OrderStatusAction, string> = {
  activate: "Active",
  pause: "Pause",
  cancel: "Cancel",
};

const ACTION_ICON: Record<
  OrderStatusAction,
  React.FC<React.SVGProps<SVGSVGElement>>
> = {
  activate: ActiveIcon,
  pause: PausedIcon,
  cancel: CanceledIcon,
};

const ACTION_TONE: Record<OrderStatusAction, string> = {
  activate: styles["item-activate"],
  pause: styles["item-pause"],
  cancel: styles["item-cancel"],
};

// Default menu per trigger status: active/launch offer Pause + Cancel,
// paused offers Active + Cancel (Figma Launch/Open, Active/Open, Paused/Open).
const DEFAULT_ITEMS: Partial<
  Record<OrderTimelineStatus, OrderStatusMenuItem[]>
> = {
  launch: [{ action: "pause" }, { action: "cancel" }],
  active: [{ action: "pause" }, { action: "cancel" }],
  paused: [{ action: "activate" }, { action: "cancel" }],
};

/** Dropdown panel: white card, toned hover per action (Figma 124:16040). */
export const OrderStatusMenu: React.FC<OrderStatusMenuProps> = ({
  items,
  onSelect,
  className,
}) => (
  <div className={cs(styles.menu, className)} role="menu">
    {items.map((item) => {
      const Icon = ACTION_ICON[item.action];
      return (
        <button
          key={item.action}
          type="button"
          role="menuitem"
          className={cs(styles.menuItem, ACTION_TONE[item.action])}
          onClick={() => onSelect?.(item.action)}
        >
          <span className={styles.menuIcon}>
            <Icon />
          </span>
          {item.label ?? ACTION_LABEL[item.action]}
        </button>
      );
    })}
  </div>
);

/**
 * Badge trigger + floating actions menu (Figma 113:4304). Uncontrolled by
 * default; pass `open`/`onOpenChange` to control it. Presentational — the
 * container owns what each action does via `onSelect`.
 */
export const OrderStatusDropdown: React.FC<OrderStatusDropdownProps> = ({
  status,
  items,
  onSelect,
  open,
  onOpenChange,
  label,
  className,
}) => {
  const [innerOpen, setInnerOpen] = useState(false);
  const isOpen = open ?? innerOpen;
  const wrapRef = useRef<HTMLDivElement>(null);
  const [dropUp, setDropUp] = useState(false);

  const setOpen = (next: boolean) => {
    setInnerOpen(next);
    onOpenChange?.(next);
  };

  useEffect(() => {
    if (!isOpen) return;
    const onOutside = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onOutside);
    return () => document.removeEventListener("mousedown", onOutside);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // The menu floats inside whatever scrolls the row (a table, the page). If
  // it would be cut off at the bottom of it, it opens upwards instead.
  useLayoutEffect(() => {
    const wrap = wrapRef.current;
    const menu = wrap?.lastElementChild;
    if (!isOpen || !wrap || !menu) {
      setDropUp(false);
      return;
    }
    let top = 0;
    let bottom = window.innerHeight;
    for (let el = wrap.parentElement; el; el = el.parentElement) {
      if (getComputedStyle(el).overflowY !== "visible") {
        const box = el.getBoundingClientRect();
        top = Math.max(top, box.top);
        bottom = Math.min(bottom, box.bottom);
        break;
      }
    }
    const badge = wrap.getBoundingClientRect();
    const needed = menu.getBoundingClientRect().height + 4;
    setDropUp(badge.bottom + needed > bottom && badge.top - needed >= top);
  }, [isOpen]);

  const menuItems = items ?? DEFAULT_ITEMS[status] ?? [];

  return (
    <div ref={wrapRef} className={cs(styles.dropdownWrap, className)}>
      <OrderStatusBadge
        status={status}
        label={label}
        open={isOpen}
        onClick={() => setOpen(!isOpen)}
      />
      {isOpen && (
        <OrderStatusMenu
          className={cs(styles.menuFloating, { [styles.menuUp]: dropUp })}
          items={menuItems}
          onSelect={(action) => {
            setOpen(false);
            onSelect?.(action);
          }}
        />
      )}
    </div>
  );
};

export default OrderTimelineRow;
